import { datadogRum } from '@datadog/browser-rum'
import { addBreadcrumb, captureMessage } from '@sentry/browser'
import axios, { AxiosResponse } from 'axios'

import {
    CEREBRO_ACCESS_TOKEN,
    CEREBRO_ID_TOKEN,
    CEREBRO_REFRESH_TOKEN,
} from 'const/localStorage'
import { Login } from 'types/resources/auth'

import { setCerebroAuthTokens } from './authTokens'
import { retryBackoffMs, waitForRetryOpportunity } from './connectivity'
import { readJwtUserId } from './jwtClaims'

const REFRESH_LOCK_NAME = 'cerebro-session-refresh'

const REFRESH_TIMEOUT_MS = 10_000

// A timed-out refresh may still have rotated the token on the server; the retry
// then spends a revoked token and is rejected, which signs the user out, so the
// timeout errs long. Without Web Locks, a tab whose refresh was rejected
// watches shared storage for another tab's tokens (the settle window); that
// must cover a full refresh in the other tab, so it matches the timeout. A
// refresh that never got an answer is retried with backoff, or sooner when the
// browser comes back online or the tab is shown again.
export const sessionRefreshTiming = {
    refreshTimeoutMs: REFRESH_TIMEOUT_MS,
    settleTimeoutMs: REFRESH_TIMEOUT_MS,
    settlePollMs: 250,
    retryBaseMs: 2_000,
    retryMaxMs: 60_000,
}

export const sessionRefreshEnvironment = {
    reload: (): void => window.location.reload(),
}

// Bare client: the refresh must not pass through the interceptor it serves.
const oauthClient = axios.create({
    baseURL: CEREBRO_API_ENDPOINT,
    validateStatus: (status) => status >= 200 && status < 500,
    headers: {
        'Content-Type': 'application/json',
        'x-dsi-client-name': 'forge',
    },
})

let inFlightRecovery: Promise<string | null> | null = null

function storedAccessTokenIfRotated(
    rejectedToken: string | null
): string | null {
    const current = localStorage.getItem(CEREBRO_ACCESS_TOKEN)
    return current && current !== rejectedToken ? current : null
}

const oauthClientAuth = {
    username: CEREBRO_OAUTH_CLIENT_ID,
    password: CEREBRO_OAUTH_CLIENT_SECRET,
}

// Sent through the bare client: the shared revoke helper lives in a module
// that already depends on this one.
function revokeOrphanedRefreshToken(refreshToken: string): void {
    oauthClient
        .post(
            '/oauth/revoke_token/',
            { token: refreshToken, token_type_hint: 'refresh_token' },
            { auth: oauthClientAuth }
        )
        .catch(() => {})
}

const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => {
        setTimeout(resolve, ms)
    })

// `settled` is final: the new access token, or null when the session ended
// while the request was out. `rejected` is any other final outcome, usually
// Cerebro refusing the refresh token, and also an answer that cannot be used
// or stored. `unanswered` means no verdict came back (offline, network change,
// timeout, throttling, server error), so the token is still worth keeping.
type RefreshAttempt =
    | { kind: 'settled'; accessToken: string | null }
    | { kind: 'rejected'; reason: string }
    | { kind: 'unanswered'; reason: string }

type SessionTokens = Pick<
    Login,
    'access_token' | 'refresh_token' | 'id_token' | 'raider_id_token'
>

function hasSessionTokens(data: unknown): data is SessionTokens {
    if (typeof data !== 'object' || data === null) {
        return false
    }
    const tokens = data as Record<string, unknown>
    const isToken = (value: unknown): boolean =>
        typeof value === 'string' && value.trim() !== ''
    return (
        isToken(tokens.access_token) &&
        isToken(tokens.refresh_token) &&
        isToken(tokens.id_token) &&
        isToken(tokens.raider_id_token)
    )
}

const settled = (accessToken: string | null): RefreshAttempt => ({
    kind: 'settled',
    accessToken,
})

// The statuses below 500 that say "try again later" rather than give a
// verdict; 5xx responses are rejected by the client's validateStatus and land
// in the catch. Every other status is final, so a persistent refusal (a WAF
// 403, a missing endpoint) ends the session instead of retrying forever.
const RETRYABLE_STATUSES = new Set([408, 429])

function describeUnanswered(error: unknown, timedOut: boolean): string {
    if (timedOut) {
        return 'timeout'
    }
    if (axios.isAxiosError(error) && error.response) {
        return `status ${error.response.status}`
    }
    return 'network'
}

async function requestRefreshedTokens(
    refreshToken: string
): Promise<RefreshAttempt> {
    // An abort signal rather than axios' `timeout`, which not every adapter
    // enforces.
    const abort = new AbortController()
    const abortTimer = setTimeout(
        () => abort.abort(),
        sessionRefreshTiming.refreshTimeoutMs
    )
    let response: AxiosResponse<unknown>
    try {
        response = await oauthClient.post(
            '/oauth/token/',
            { grant_type: 'refresh_token', refresh_token: refreshToken },
            {
                auth: oauthClientAuth,
                signal: abort.signal,
            }
        )
    } catch (error) {
        return {
            kind: 'unanswered',
            reason: describeUnanswered(error, abort.signal.aborted),
        }
    } finally {
        clearTimeout(abortTimer)
    }
    if (RETRYABLE_STATUSES.has(response.status)) {
        return { kind: 'unanswered', reason: `status ${response.status}` }
    }
    if (response.status !== 200) {
        return { kind: 'rejected', reason: `status ${response.status}` }
    }
    if (!hasSessionTokens(response.data)) {
        return { kind: 'rejected', reason: 'malformed response' }
    }
    const { access_token, refresh_token, id_token, raider_id_token } =
        response.data
    // Storage moved on while the request was out: a sign-out (tokens gone)
    // must not be undone by a late response, and another tab's rotation is
    // already what every tab reads.
    const storedRefreshToken = localStorage.getItem(CEREBRO_REFRESH_TOKEN)
    if (storedRefreshToken !== refreshToken) {
        if (!storedRefreshToken) {
            revokeOrphanedRefreshToken(refresh_token)
            return settled(null)
        }
        return settled(localStorage.getItem(CEREBRO_ACCESS_TOKEN))
    }
    try {
        setCerebroAuthTokens(
            access_token,
            refresh_token,
            id_token,
            raider_id_token
        )
    } catch {
        return { kind: 'rejected', reason: 'storage error' }
    }
    return settled(access_token)
}

// Without Web Locks two tabs can refresh at once, and with no grace period on
// rotation the loser's refresh is rejected even though the session survived.
// The loser recovers by waiting for the winner's tokens to land in storage.
async function waitForTokensFromAnotherTab(
    rejectedToken: string | null
): Promise<string | null> {
    const deadline = Date.now() + sessionRefreshTiming.settleTimeoutMs
    while (Date.now() < deadline) {
        await sleep(sessionRefreshTiming.settlePollMs)
        const rotated = storedAccessTokenIfRotated(rejectedToken)
        if (rotated) {
            return rotated
        }
        // Signed out elsewhere: no tokens are coming.
        if (!localStorage.getItem(CEREBRO_REFRESH_TOKEN)) {
            return null
        }
    }
    return null
}

async function refreshSession(
    rejectedToken: string | null,
    holdsCrossTabLock: boolean
): Promise<RefreshAttempt> {
    const rotated = storedAccessTokenIfRotated(rejectedToken)
    if (rotated) {
        return settled(rotated)
    }
    const refreshToken = localStorage.getItem(CEREBRO_REFRESH_TOKEN)
    if (!refreshToken) {
        return settled(null)
    }
    const attempt = await requestRefreshedTokens(refreshToken)
    // Holding the lock, no other tab can be refreshing, so nothing will arrive.
    if (attempt.kind !== 'rejected' || holdsCrossTabLock) {
        return attempt
    }
    const recovered = await waitForTokensFromAnotherTab(rejectedToken)
    return recovered ? settled(recovered) : attempt
}

async function withCrossTabLock(
    task: (holdsCrossTabLock: boolean) => Promise<RefreshAttempt>
): Promise<RefreshAttempt> {
    const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
    if (!locks) {
        return task(false)
    }
    const attempt: RefreshAttempt = await locks.request(REFRESH_LOCK_NAME, () =>
        task(true)
    )
    return attempt
}

// The browser drops a failed request's details, and a sleeping laptop has no
// monitoring session, so each outcome is recorded where it can still be seen:
// a breadcrumb for every unanswered attempt, and an event once the session is
// either recovered or lost.
function reportUnanswered(reason: string, failureCount: number): void {
    addBreadcrumb({
        category: 'auth',
        level: 'warning',
        message: 'Session refresh got no answer',
        data: { reason, failureCount },
    })
    datadogRum.addAction('session_refresh_unanswered', {
        reason,
        failureCount,
    })
}

// Unanswered attempts tallied per reason: an outage can last hours, so the
// report stays a handful of counts rather than one entry per retry.
type FailureReasonCounts = Record<string, number>

function reportRecovered(failureReasons: FailureReasonCounts): void {
    captureMessage('Session refresh recovered after going unanswered', {
        level: 'info',
        extra: { failureReasons },
    })
    datadogRum.addAction('session_refresh_recovered', { failureReasons })
}

function reportRejected(
    reason: string,
    failureReasons: FailureReasonCounts
): void {
    captureMessage('Session refresh rejected', {
        level: 'warning',
        extra: { reason, earlierFailureReasons: failureReasons },
    })
    datadogRum.addAction('session_refresh_rejected', {
        reason,
        earlierFailureReasons: failureReasons,
    })
}

// Only a final answer ends the session. A refresh that went unanswered keeps
// the tokens and tries again, each round re-reading storage so another tab's
// rotation or sign-out is honoured.
async function refreshUntilAnswered(
    rejectedToken: string | null
): Promise<string | null> {
    const failureReasons: FailureReasonCounts = {}
    let failureCount = 0
    for (;;) {
        const attempt = await withCrossTabLock((holdsCrossTabLock) =>
            refreshSession(rejectedToken, holdsCrossTabLock)
        )
        if (attempt.kind === 'settled') {
            if (attempt.accessToken && failureCount > 0) {
                reportRecovered(failureReasons)
            }
            return attempt.accessToken
        }
        if (attempt.kind === 'rejected') {
            reportRejected(attempt.reason, failureReasons)
            return null
        }
        failureCount += 1
        failureReasons[attempt.reason] =
            (failureReasons[attempt.reason] ?? 0) + 1
        reportUnanswered(attempt.reason, failureCount)
        // The lock is not held while waiting, so another tab can recover the
        // session meanwhile.
        await waitForRetryOpportunity(
            retryBackoffMs(failureCount, sessionRefreshTiming)
        )
    }
}

function recoverSharedSession(
    rejectedToken: string | null
): Promise<string | null> {
    const rotated = storedAccessTokenIfRotated(rejectedToken)
    if (rotated) {
        return Promise.resolve(rotated)
    }
    if (!localStorage.getItem(CEREBRO_REFRESH_TOKEN)) {
        return Promise.resolve(null)
    }
    if (!inFlightRecovery) {
        inFlightRecovery = refreshUntilAnswered(rejectedToken).finally(() => {
            inFlightRecovery = null
        })
    }
    return inFlightRecovery
}

export const storedSessionUserId = (): string | null =>
    readJwtUserId(localStorage.getItem(CEREBRO_ID_TOKEN))

/**
 * Resolve a usable access token after `rejectedToken` got a 401, or null when
 * the session cannot be recovered: Cerebro refused the refresh token, or the
 * session was signed out. A refresh that gets no answer is retried until one
 * of those, so the promise stays pending while Cerebro is unreachable rather
 * than ending a session that is still valid. Refreshes at most once per tab at
 * a time and holds a cross-tab Web Lock so only one tab spends the shared
 * refresh token.
 *
 * `sessionUserId` is the user the rejected request was sent as; it defaults to
 * the stored one for callers that cannot tell. If another tab signed in as
 * someone else meanwhile, the stored tokens belong
 * to that account: replaying with them would act as the wrong user, and a 401
 * would make the caller sign out, revoking the other account's session. The
 * tab reloads instead, as the cross-tab sync would moments later, and the
 * request never settles.
 */
export async function recoverAccessToken(
    rejectedToken: string | null,
    sessionUserId: string | null = storedSessionUserId()
): Promise<string | null> {
    const accessToken = await recoverSharedSession(rejectedToken)
    const currentUserId = storedSessionUserId()
    if (sessionUserId && currentUserId && currentUserId !== sessionUserId) {
        sessionRefreshEnvironment.reload()
        return new Promise<never>(() => {})
    }
    return accessToken
}
